if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this._defaultApplied = false;
      this._observer = null;
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll(
        ".m-product-option select, .m-product-option input"
      );
      const selectedValues = new Map();

      // Read currently selected
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (field.tagName.toLowerCase() === "input" && field.type === "radio") {
          if (field.checked) {
            selectedValues.set(fieldName, field.dataset.optionValueId);
          }
        }
      });

      // Fallback to first valid (logic)
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find((opt) => !opt.disabled);
            if (firstValidOption) {
              selectedValues.set(fieldName, firstValidOption.dataset.optionValueId);
            }
          } else if (field.tagName.toLowerCase() === "input" && field.type === "radio") {
            selectedValues.set(fieldName, field.dataset.optionValueId);
          }
        }
      });

      // Unique list in DOM order
      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) result.push(optionValueId);
        }
      });

      return result;
    }

    /**
     * Applies "first option selected" in the UI (not just in logic).
     * Returns the last changed element so we can emit Minimog event properly.
     */
    applyDefaultsToUI() {
      let lastChangedEl = null;

      // Handle each option block independently (more reliable than global grouping)
      const optionBlocks = this.querySelectorAll(".m-product-option");
      optionBlocks.forEach((block) => {
        // 1) Dropdown
        const select = block.querySelector("select");
        if (select) {
          const hasValidSelected = Array.from(select.options).some(
            (opt) => opt.selected && !opt.disabled
          );

          if (!hasValidSelected) {
            const firstEnabled = Array.from(select.options).find((opt) => !opt.disabled);
            if (firstEnabled) {
              // Make it actually selected
              select.value = firstEnabled.value;
              Array.from(select.options).forEach((o) => (o.selected = o.value === firstEnabled.value));
              lastChangedEl = select;
            }
          }
          return; // if select exists, we don't expect radios in same block
        }

        // 2) Radios
        const radios = Array.from(block.querySelectorAll('input[type="radio"]'));
        if (radios.length) {
          const anyChecked = radios.some((r) => r.checked);

          if (!anyChecked) {
            const firstEnabledRadio = radios.find((r) => !r.disabled);
            if (firstEnabledRadio) {
              firstEnabledRadio.checked = true;
              lastChangedEl = firstEnabledRadio;
            }
          }
        }
      });

      return lastChangedEl;
    }

    emitMinimogChange(targetEl, event) {
      // Minimog expects a "target" that is either:
      // - selected option element for SELECT
      // - the radio input itself
      const target = this.getInputForEventTarget(targetEl);

      MinimogEvents.emit(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        {
          data: {
            selectedOptionValues: this.selectedOptionValues,
            event,
            target,
          },
        }
      );
    }

    runDefaultSelection(force = false) {
      // Avoid looping forever
      if (this._defaultApplied && !force) return;

      const changedEl = this.applyDefaultsToUI();
      if (!changedEl) {
        // Nothing to change (maybe already selected)
        this._defaultApplied = true;
        return;
      }

      // Trigger native change so other listeners update
      const evt = new Event("change", { bubbles: true });
      changedEl.dispatchEvent(evt);

      // Emit Minimog event with proper target
      this.emitMinimogChange(changedEl, evt);

      this._defaultApplied = true;
    }

    connectedCallback() {
      // Run once immediately
      this.runDefaultSelection();

      // Run again in next frame (common Minimog re-render timing)
      requestAnimationFrame(() => this.runDefaultSelection(true));

      // Observe for re-renders that replace option DOM
      if (!this._observer) {
        this._observer = new MutationObserver(() => {
          // If theme re-rendered and removed selection, re-apply
          this._defaultApplied = false;
          this.runDefaultSelection();
        });

        this._observer.observe(this, { childList: true, subtree: true });
      }

      // Keep existing change behavior
      this.addEventListener("change", (event) => {
        const targetEl = event.target;
        this.emitMinimogChange(targetEl, event);
      });
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    getInputForEventTarget(target) {
      if (!target) return target;
      return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
